import React, { ReactElement, useMemo } from 'react'
import './EventTextMarkdown.css'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import rehypeWrap from 'rehype-wrap-all'
import rehypeTagNameChange from '@/util/rehype-custom-plugins/rehype-tag-swap'
import { useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { readAgentCitationSpanProps, rehypeAgentCitations } from '@/util/rehype-custom-plugins/rehype-agent-citations'
import AgentResponseCitationBadge from './agent-response/AgentResponseCitationBadge'
import LegalCitationLink from '@/agent/references/components/LegalCitationLink'
import { useFootnoteInfoByCitationId } from '@/agent/references/hooks/useFootnoteInfoByCitationId'
import { FootnoteInfoByCitationId, getCitationLabelFromFootnoteInfo } from '@/agent/references/util/agent-reference-footnotes'

interface EventTextMarkdownProps {
  conversationId: string
  eventId: string
  /** When provided (e.g. from the parent bubble), footnote data is computed once per message. */
  footnoteInfoByCitationId?: FootnoteInfoByCitationId
}

type EventTextMarkdownBodyProps = {
  trimmedText: string
  footnoteInfoByCitationId: FootnoteInfoByCitationId
}

function EventTextMarkdownBody(props: EventTextMarkdownBodyProps): ReactElement {
  const { trimmedText, footnoteInfoByCitationId } = props

  return (
    <Markdown
      className="markdown-bubble-container max-w-3xl"
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[
        rehypeRaw,
        [rehypeWrap, { selector: 'table', wrapper: 'div.source-html-table-wrapper' }],
        rehypeAgentCitations,
        [
          // Swap pre and code tags with div tags to fix markdown rendering issues
          rehypeTagNameChange,
          [
            { tagName: 'pre', newTagName: 'div' },
            { tagName: 'code', newTagName: 'div' },
          ],
        ],
      ]}
      components={{
        // Handle divs
        div: ({ children, ...divProps }) => {
          // map the children and if there is a child string that contains any more than 2 '/n' for example
          // "\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n\n" then repalce it with a max of a 2 new line '\n\n'
          const newLineRegex = /\n{2,}/g
          const filteredChildren = Array.isArray(children)
            ? children.map((child) => {
                if (typeof child === 'string') {
                  return child.replace(newLineRegex, '\n\n')
                }
                return child
              })
            : children

          return <div {...divProps}>{filteredChildren}</div>
        },

        // Handle list items
        li: ({ children, ...liProps }) => {
          // filter the children and if there is a child string of '\n' then remove it
          const filteredChildren = Array.isArray(children) ? children.filter((child) => child !== '\n') : children

          return <li {...liProps}>{filteredChildren}</li>
        },

        // Handle citation spans
        span: ({ node, ...spanProps }) => {
          const citation = readAgentCitationSpanProps(spanProps)
          if (!citation) return <span {...spanProps}>{spanProps.children}</span>

          const citationLabel = getCitationLabelFromFootnoteInfo(footnoteInfoByCitationId[citation.citationId], citation.citationIdIndex)

          // Legal citation link: the authority text itself is the click target
          if (citation.isLegalCitationLink) {
            return (
              <LegalCitationLink citation_id={citation.citationId} citation_id_index={citation.citationIdIndex} citationLabel={citationLabel}>
                {spanProps.children}
              </LegalCitationLink>
            )
          }

          return <AgentResponseCitationBadge citation_id={citation.citationId} citation_id_index={citation.citationIdIndex} citationLabel={citationLabel} />
        },
      }}
    >
      {trimmedText}
    </Markdown>
  )
}

/**
 * Event Text Markdown
 *
 * Renders the event's value (text response) as Markdown.
 */
const EventTextMarkdown: React.FC<EventTextMarkdownProps> = (props): ReactElement | null => {
  const { conversationId, eventId, footnoteInfoByCitationId: providedFootnoteInfo } = props
  const event = useAppSelector((state: RootState) => state.agentEventsState.events[conversationId]?.[eventId])
  const trimmedText = useMemo(() => event?.value?.trim() ?? '', [event?.value])
  const computedFootnoteInfo = useFootnoteInfoByCitationId(event?.turn_id ?? null, trimmedText)
  const footnoteInfoByCitationId = providedFootnoteInfo ?? computedFootnoteInfo

  if (!event?.value) {
    console.error('EventTextMarkdown: Event or event value is missing.')
    return null
  }

  return <EventTextMarkdownBody trimmedText={trimmedText} footnoteInfoByCitationId={footnoteInfoByCitationId} />
}

export default EventTextMarkdown
